import {
	Box,
	Chip,
	IconButton,
	TextField,
	Autocomplete,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Grid,
} from "@mui/material";
import { Visibility, Edit, Add, CheckCircle } from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import { useEffect, useState, useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import MDButton from "../../components/MDButton";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import apiService from "../../services/api-service";

const NonConformitaDashboard = () => {
	const navigate = useNavigate();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [cantieri, setCantieri] = useState([]);
	const [filters, setFilters] = useState({
		cantiere_id: null,
		stato: null,
	});
	const [selectedNC, setSelectedNC] = useState(null);
	const [detailDialogOpen, setDetailDialogOpen] = useState(false);

	const stati = [
		{ value: "APERTA", label: "Aperta" },
		{ value: "IN_SOLUZIONE", label: "In Soluzione" },
		{ value: "CHIUSA_POSITIVA", label: "Chiusa Positiva" },
		{ value: "CHIUSA_NEGATIVA", label: "Chiusa Negativa" },
	];

	useEffect(() => {
		fetchCantieri();
		fetchNC();
	}, [filters]);

	const fetchCantieri = async () => {
		try {
			const response = await apiService.get("/area-management/", {}, {});
			if (response && Array.isArray(response)) {
				setCantieri(response.filter((area) => area.yard === true));
			}
		} catch (error) {
			toast.error("Errore caricamento cantieri");
		}
	};

	const fetchNC = async () => {
		setLoading(true);
		try {
			const params = {};
			if (filters.cantiere_id) params.cantiere_id = filters.cantiere_id;
			if (filters.stato) params.stato = filters.stato;

			const response = await apiService.get("/non-conformita/", params, {});
			setData(response || []);
		} catch (error) {
			toast.error("Errore caricamento Non Conformità");
			setData([]);
		} finally {
			setLoading(false);
		}
	};

	const handleViewDetail = async (ncId) => {
		try {
			const response = await apiService.get(`/non-conformita/${ncId}/`, {}, {});
			setSelectedNC(response);
			setDetailDialogOpen(true);
		} catch (error) {
			toast.error("Errore caricamento dettaglio");
		}
	};

	const handleEdit = (ncId) => {
		navigate(`/quality/non-conformita/edit/${ncId}`);
	};

	const handleProponeSoluzione = (ncId) => {
		navigate(`/quality/non-conformita/${ncId}/soluzione`);
	};

	const handleChiudi = (ncId) => {
		navigate(`/quality/non-conformita/${ncId}/chiusura`);
	};

	const getStatoColor = (stato) => {
		switch (stato) {
			case "APERTA":
				return "error";
			case "IN_SOLUZIONE":
				return "warning";
			case "CHIUSA_POSITIVA":
				return "success";
			case "CHIUSA_NEGATIVA":
				return "default";
			default:
				return "default";
		}
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "numero",
				header: "N° NC",
				size: 120,
			},
			{
				accessorKey: "cantiere_nome",
				header: "Cantiere",
				size: 200,
			},
			{
				accessorKey: "stato_display",
				header: "Stato",
				size: 150,
				Cell: ({ cell, row }) => <Chip label={cell.getValue()} color={getStatoColor(row.original.stato)} size="small" />,
			},
			{
				accessorKey: "data_rilevazione",
				header: "Data Rilevazione",
				size: 150,
				Cell: ({ cell }) => new Date(cell.getValue()).toLocaleDateString("it-IT"),
			},
			{
				accessorKey: "rilevata_da_nome",
				header: "Rilevata da",
				size: 180,
			},
			{
				accessorKey: "descrizione",
				header: "Descrizione",
				size: 300,
				Cell: ({ cell }) => (
					<span title={cell.getValue()}>
						{cell.getValue().length > 60 ? cell.getValue().substring(0, 60) + "..." : cell.getValue()}
					</span>
				),
			},
		],
		[]
	);

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box p={3}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
					<Typography variant="h4" color="primary">
						Non Conformità
					</Typography>
					<MDButton
						variant="contained"
						color="success"
						startIcon={<Add />}
						onClick={() => navigate("/quality/non-conformita/add")}
					>
						Nuova NC
					</MDButton>
				</Box>

				{/* Filtri */}
				<Box display="flex" gap={2} mb={3}>
					<Autocomplete
						options={cantieri}
						getOptionLabel={(option) => option.name || ""}
						value={cantieri.find((c) => c.id === filters.cantiere_id) || null}
						onChange={(event, newValue) => {
							setFilters((prev) => ({ ...prev, cantiere_id: newValue ? newValue.id : null }));
						}}
						renderInput={(params) => <TextField {...params} label="Cantiere" />}
						sx={{ minWidth: 250 }}
					/>
					<Autocomplete
						options={stati}
						getOptionLabel={(option) => option.label || ""}
						value={stati.find((s) => s.value === filters.stato) || null}
						onChange={(event, newValue) => {
							setFilters((prev) => ({ ...prev, stato: newValue ? newValue.value : null }));
						}}
						renderInput={(params) => <TextField {...params} label="Stato" />}
						sx={{ minWidth: 200 }}
					/>
					<Button variant="outlined" onClick={() => setFilters({ cantiere_id: null, stato: null })}>
						Reset Filtri
					</Button>
				</Box>

				{/* Tabella */}
				<MaterialReactTable
					columns={columns}
					data={data}
					enableRowActions
					positionActionsColumn="last"
					renderRowActions={({ row }) => (
						<Box display="flex" gap={1}>
							<IconButton size="small" onClick={() => handleViewDetail(row.original.id)} title="Visualizza">
								<Visibility />
							</IconButton>
							{row.original.stato === "APERTA" && (
								<>
									<IconButton size="small" onClick={() => handleEdit(row.original.id)} title="Modifica">
										<Edit />
									</IconButton>
									<IconButton
										size="small"
										onClick={() => handleProponeSoluzione(row.original.id)}
										title="Proponi Soluzione"
										color="warning"
									>
										<CheckCircle />
									</IconButton>
								</>
							)}
							{row.original.stato === "IN_SOLUZIONE" && (
								<IconButton size="small" onClick={() => handleChiudi(row.original.id)} title="Chiudi NC" color="success">
									<CheckCircle />
								</IconButton>
							)}
						</Box>
					)}
					state={{ isLoading: loading }}
					enableSorting
					enableColumnFilters={false}
					enablePagination
					initialState={{ pagination: { pageSize: 10 } }}
				/>

				{/* Dialog Dettaglio */}
				<Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
					<DialogTitle>Dettaglio Non Conformità</DialogTitle>
					<DialogContent>
						{selectedNC && (
							<Grid container spacing={2} mt={1}>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Numero NC
									</Typography>
									<Typography variant="body1" fontWeight="bold">
										{selectedNC.numero}
									</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Stato
									</Typography>
									<Chip label={selectedNC.stato_display} color={getStatoColor(selectedNC.stato)} size="small" />
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Cantiere
									</Typography>
									<Typography variant="body1">{selectedNC.cantiere_nome}</Typography>
								</Grid>
								<Grid item xs={6}>
									<Typography variant="body2" color="textSecondary">
										Data Rilevazione
									</Typography>
									<Typography variant="body1">{new Date(selectedNC.data_rilevazione).toLocaleString("it-IT")}</Typography>
								</Grid>
								<Grid item xs={12}>
									<Typography variant="body2" color="textSecondary">
										Rilevata da
									</Typography>
									<Typography variant="body1">{selectedNC.rilevata_da_nome}</Typography>
								</Grid>
								<Grid item xs={12}>
									<Typography variant="body2" color="textSecondary">
										Descrizione
									</Typography>
									<Typography variant="body1">{selectedNC.descrizione}</Typography>
								</Grid>

								{/* Firma Apertura */}
								{selectedNC.firma_apertura && (
									<Grid item xs={12}>
										<Typography variant="body2" color="textSecondary">
											Firma Apertura
										</Typography>
										<Box mt={1} display="flex" justifyContent="center">
											<img
												src={selectedNC.firma_apertura}
												alt="Firma digitale"
												style={{ maxWidth: "400px", border: "1px solid #ccc", borderRadius: "4px" }}
											/>
										</Box>
									</Grid>
								)}

								{/* Soluzione */}
								{selectedNC.soluzione && (
									<>
										<Grid item xs={12}>
											<Typography variant="h6" color="primary" mt={2}>
												Soluzione Proposta
											</Typography>
										</Grid>
										<Grid item xs={6}>
											<Typography variant="body2" color="textSecondary">
												Tipo Soluzione
											</Typography>
											<Typography variant="body1">{selectedNC.soluzione.tipo_soluzione_display}</Typography>
										</Grid>
										<Grid item xs={6}>
											<Typography variant="body2" color="textSecondary">
												Assegnato a
											</Typography>
											<Typography variant="body1">{selectedNC.soluzione.assegnato_a_nome}</Typography>
										</Grid>
										<Grid item xs={12}>
											<Typography variant="body2" color="textSecondary">
												Descrizione Soluzione
											</Typography>
											<Typography variant="body1">{selectedNC.soluzione.descrizione}</Typography>
										</Grid>
									</>
								)}

								{/* Chiusura */}
								{selectedNC.chiusura && (
									<>
										<Grid item xs={12}>
											<Typography variant="h6" color="primary" mt={2}>
												Chiusura
											</Typography>
										</Grid>
										<Grid item xs={4}>
											<Typography variant="body2" color="textSecondary">
												Esito
											</Typography>
											<Chip
												label={selectedNC.chiusura.esito_positivo ? "Positivo" : "Negativo"}
												color={selectedNC.chiusura.esito_positivo ? "success" : "error"}
												size="small"
											/>
										</Grid>
										<Grid item xs={4}>
											<Typography variant="body2" color="textSecondary">
												Costo
											</Typography>
											<Typography variant="body1">€ {selectedNC.chiusura.costo}</Typography>
										</Grid>
										<Grid item xs={4}>
											<Typography variant="body2" color="textSecondary">
												Tempo (ore)
											</Typography>
											<Typography variant="body1">{selectedNC.chiusura.tempo_rilavorazione}h</Typography>
										</Grid>
									</>
								)}
							</Grid>
						)}
					</DialogContent>
					<DialogActions>
						<Button onClick={() => setDetailDialogOpen(false)}>Chiudi</Button>
					</DialogActions>
				</Dialog>
			</Box>
		</DashboardLayout>
	);
};

export default NonConformitaDashboard;
